import React from 'react'
import clsx from 'clsx'
import { useDrag, useDrop } from 'react-dnd'
import { Row } from '@tanstack/react-table'
import { AnimatePresence } from 'framer-motion'

import DragIcon from '../../../../icons/DragIcon'
import TaskRowRightClickMenu from '../TaskRowRightClickMenu'
import CellComponent, { CellComponentProps } from '../CellComponent'
import ShareTaskModal from '@SonarCX/components/molecules/ShareTaskModal'
import useTaskRightClickMenu from '@SonarCX/hooks/useTaskRightClickMenu'

import useStyles from './styles'

export type RowComponentProps<T> = {
  row: Row<T>
  index: number
  rowKey: string | number
  isExpanded: boolean
  isTarget: React.MutableRefObject<any>
  blinkRow: boolean
  getRowClass: (row: Row<T>) => string
  targetRef: React.MutableRefObject<any>
  disableSkeleton: boolean
  allowDragDropSort: boolean
  onDragDrop: (
    sourceID: string | number,
    targetID: string | number,
    after?: boolean
  ) => void
  dragDropID: string
  alternateDropCallback: boolean
  enableResizing: boolean
  enableCustomContextMenu?: boolean
} & Omit<
  CellComponentProps<T>,
  'cellKey' | 'index' | 'row' | 'cell' | 'isExpanded'
>

const RowComponent = <T extends unknown>({
  row,
  index,
  rowKey,
  isExpanded,
  isTarget,
  blinkRow,
  onRowClicked,
  getRowClass,
  targetRef,
  renderRowSubComponent,
  disableSkeleton,
  allowDragDropSort,
  onDragDrop,
  dragDropID = 'NO_DRAG_DROP',
  alternateDropCallback,
  enableResizing,
  enableCustomContextMenu,
  ...cellProps
}: RowComponentProps<T>) => {
  const [hovering, setHovering] = React.useState(false)
  const {
    setShowShare,
    handleOpen,
    handleClose,
    showShare,
    menuPosition,
    showMenu
  } = useTaskRightClickMenu()

  const handleRightClick = e => {
    if (!enableCustomContextMenu) return
    e.preventDefault()
    handleOpen(e)
  }

  const handleRightClickClose = () => {
    handleClose()
  }

  const onDrop = React.useCallback(
    (after: boolean) => (droppedItem: any) => {
      setHovering(false)

      const sourceID = droppedItem.id
      // @ts-ignore
      const targetID = row.original.id
      if (sourceID === targetID) {
        return
      }

      const sourceIndex: number = droppedItem.index
      const targetIndex = index
      if (sourceIndex === targetIndex) {
        return
      }

      if (alternateDropCallback) {
        onDragDrop(sourceIndex, targetIndex + (after ? 1 : 0))
        return
      }

      onDragDrop(sourceID, targetID, after)
    },
    // @ts-ignore
    [row.original.id, onDragDrop, alternateDropCallback, index]
  )

  const [{ opacity }, dragRef, previewRef] = useDrag({
    // @ts-ignore
    item: { type: dragDropID, id: row.original.id, index },
    collect: monitor => ({
      opacity: monitor.isDragging() ? 0.4 : 1
    })
  })

  const [{ isOver: isOverTop }, dropTop] = useDrop({
    accept: dragDropID,
    drop: onDrop(false),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const [{ isOver: isOverBottom }, dropBottom] = useDrop({
    accept: dragDropID,
    drop: onDrop(true),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const startHovering = React.useCallback(() => setHovering(true), [])

  const endHovering = React.useCallback(() => setHovering(false), [])

  const hasSubComponent = React.useMemo(
    () => isExpanded && !row.getIsGrouped() && renderRowSubComponent,
    [isExpanded, row, renderRowSubComponent]
  )

  const numCells = React.useMemo(() => row.getVisibleCells().length, [row])

  const classes = useStyles({ isOverTop, isOverBottom })

  return (
    <>
      {allowDragDropSort && (
        <tr>
          <td
            ref={dropTop}
            colSpan={numCells}
            className={clsx(classes.drop, classes.dropTop)}
          />
        </tr>
      )}
      <tr
        onMouseEnter={startHovering}
        onMouseLeave={endHovering}
        className={clsx(
          'table-row',
          (row.getIsGrouped() || onRowClicked) && 'clickable',
          getRowClass && getRowClass(row),
          isTarget && 'blink',
          blinkRow && 'updated-row',
          showMenu && classes.forceHover
        )}
        style={allowDragDropSort ? { borderBottom: 'none', opacity } : {}}
        ref={el => {
          if (isTarget && targetRef) {
            targetRef.current = el
          }
          previewRef(el)
        }}
        onContextMenu={handleRightClick}
      >
        {row.getVisibleCells().map((cell, i) => {
          const cellKey = `${rowKey}-${i}`
          const defaultStyle = enableResizing
            ? {
                minWidth: cell.column.getSize(),
                width: cell.column.getSize(),
                maxWidth: cell.column.getSize()
              }
            : {}

          return (
            <td
              key={cell.id}
              onClick={e => {
                e.stopPropagation()

                // @ts-ignore
                if (e.target.className === 'table-data') {
                  onRowClicked && onRowClicked(row)
                }
              }}
              className='table-data'
              style={
                i === 0
                  ? {
                      ...defaultStyle,
                      paddingRight: '1rem',
                      whiteSpace: 'nowrap',
                      ...(allowDragDropSort
                        ? {
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: '36px'
                          }
                        : {})
                    }
                  : defaultStyle
              }
            >
              {i === 0 && allowDragDropSort ? (
                hovering ? (
                  <span ref={dragRef} className={classes.drag}>
                    <DragIcon />
                  </span>
                ) : (
                  <span className={classes.emptyDrag} />
                )
              ) : null}

              <CellComponent
                cellKey={cellKey}
                index={i}
                row={row}
                cell={cell}
                isExpanded={isExpanded}
                renderRowSubComponent={renderRowSubComponent}
                onRowClicked={onRowClicked}
                {...cellProps}
              />
            </td>
          )
        })}
      </tr>
      {allowDragDropSort && (
        <tr>
          <td
            ref={dropBottom}
            colSpan={numCells}
            className={clsx(classes.drop, classes.dropBottom)}
          />
        </tr>
      )}
      <AnimatePresence>
        {hasSubComponent && renderRowSubComponent(row)}
        {showMenu && (
          <TaskRowRightClickMenu
            menuPosition={menuPosition}
            handleClose={handleRightClickClose}
            handleShare={() => setShowShare(true)}
            task={row.original}
          />
        )}
      </AnimatePresence>
      {showShare && (
        <ShareTaskModal
          // @ts-expect-error it exists
          selected={row.original.id}
          handleOnClose={() => setShowShare(false)}
        />
      )}
    </>
  )
}

export default RowComponent
